import {
  pathnameWithoutLocale,
  sellerOrgPath,
  teachPath,
} from "@outschool/routes";
import React from "react";
import { useLocation } from "react-router-dom";

import { ACADEMICS_PARENT_SIDE_FEATURE_FLAG } from "../../shared/featureFlags";
import * as Routes from "../../shared/Routes";
import { useFeatureFlag } from "../lib/FeatureFlags";
import { UserMode, useUserMode } from "../lib/UserMode";
import useExperiment from "./useExperiment";

export const ACADEMICS_PARENT_EXPERIMENT_NAME = "AcademicsParent";

type AcademicsParentExperimentVariant =
  | "control"
  | "holdout"
  | "treatment1"
  | "treatment2"
  | "treatment3"
  | "treatment4";

const ACADEMICS_PARENT_TREATMENT_VARIANTS =
  new Set<AcademicsParentExperimentVariant>([
    "treatment1",
    "treatment2",
    "treatment3",
    "treatment4",
  ]);

type AcademicsParentSideOptions = {
  trackExposure?: boolean;
  userModeOverride?: UserMode;
};

/**
 * Whether search filters use the academics shape. Filter values are read and
 * written the same way for every experiment variant so that a URL keeps
 * filtering results on pages where the academics controls are hidden.
 */
export function useAcademicsSearchFiltersEnabled() {
  return useFeatureFlag(ACADEMICS_PARENT_SIDE_FEATURE_FLAG);
}

/**
 * Whether the current page should receive the academics updates.
 * Parent pages use the experiment treatment while the feature flag is enabled.
 * Non-parent pages continue to use only the feature flag.
 */
export function useAcademicsParentSideEnabled({
  trackExposure = false,
  userModeOverride,
}: AcademicsParentSideOptions = {}) {
  const contextUserMode = useUserMode();
  const userMode = userModeOverride ?? contextUserMode;
  const pathname = pathnameWithoutLocale(useLocation().pathname);
  const isFeatureFlagEnabled = useFeatureFlag(
    ACADEMICS_PARENT_SIDE_FEATURE_FLAG,
  );
  const { variant, trigger } = useExperiment<AcademicsParentExperimentVariant>(
    ACADEMICS_PARENT_EXPERIMENT_NAME,
  );
  const isTeacherPath =
    pathname === teachPath() || pathname.startsWith(Routes.teachBasePath());
  const isParentSide =
    userMode === UserMode.Parent &&
    !isTeacherPath &&
    !pathname.startsWith(sellerOrgPath()) &&
    !pathname.startsWith("/admin");

  React.useEffect(() => {
    if (trackExposure && isFeatureFlagEnabled && isParentSide) {
      trigger();
    }
  }, [isFeatureFlagEnabled, isParentSide, trackExposure, trigger]);

  return (
    isFeatureFlagEnabled &&
    (!isParentSide || ACADEMICS_PARENT_TREATMENT_VARIANTS.has(variant))
  );
}
